import { ApplicationRef, ComponentRef, createComponent, EnvironmentInjector, inject, Injectable, Injector, NgZone, OnDestroy, Type } from "@angular/core";
import * as L from "leaflet";
import "leaflet.markercluster";
import { Subject, takeUntil } from "rxjs";
import { MapActionsService } from "./map/map-actions.service";
import { MapDataService } from "./map/map-data.service";
import { MapStateService } from "./map/map-state.service";

@Injectable()
export abstract class BaseMapComponentsService implements OnDestroy {
    private elements: HTMLElement[] = [];
    private refs: ComponentRef<unknown>[] = [];
    private injector = inject(Injector);
    private environmentInjector = inject(EnvironmentInjector);
    private applicationRef = inject(ApplicationRef);
    protected ngZone = inject(NgZone);
    protected readonly destroy$ = new Subject<void>();
    private popUp: L.Popup;
    constructor() {
        this.mapStateService.map$.pipe(takeUntil(this.destroy$)).subscribe((map) => (this.map = map));
    }

    /**
     * Creates a host element backed by an Angular component.
     *
     * @param attachToAppRef When true (default) the view participates in the global
     * change-detection loop (`ApplicationRef.tick`). For static, display-only content
     * (map markers, cluster markers) pass `false` and call `detectChanges()` once after
     * setting the inputs — this keeps thousands of marker views out of every CD cycle.
     * Interactive content (popups with dropdowns) must stay attached.
     */
    public createElementWithComponent<C>(componentType: Type<C>, tag = "div", attachToAppRef = true): [HTMLElement, ComponentRef<C>] {
        const element = document.createElement(tag);
        const component = createComponent(componentType, {
            elementInjector: this.injector,
            environmentInjector: this.environmentInjector,
            hostElement: element,
        });
        if (attachToAppRef) {
            this.applicationRef.attachView(component.hostView);
        }
        this.refs.push(component);
        this.elements.push(element);
        return [element, component];
    }

    /**
     * Destroys and forgets every component/element created via {@link createElementWithComponent}.
     * Call this before re-drawing so marker/popup views from the previous draw don't accumulate
     * (clearing a Leaflet layer only detaches markers from the map, it never destroys their Angular views).
     */
    protected clearTrackedComponents(): void {
        this.refs.splice(0).forEach((ref) => ref.destroy());
        this.elements.splice(0).forEach((element) => element.remove());
    }

    protected updateMarkers(): void {
        this.ngZone.runOutsideAngular(() => {
            this.refs.forEach((ref) => {
                ref.changeDetectorRef.detectChanges();
            });
        });
    }

    public cleanup(): void {
        this.clearTrackedComponents();
        this.layer?.clearLayers();
        // Cluster groups are attached straight to the shared map, so clearing `layer` alone
        // would leave this mode's markers visible after switching to another map mode.
        Object.keys(this.markerGroups).forEach((id) => {
            this._markerLayerGroup?.removeLayer(this.markerGroups[id]);
            delete this.markerGroups[id];
        });
        this._markerLayerGroup?.remove();
        this._markerLayerGroup = null;
    }

    ngOnDestroy(): void {
        this.cleanup();
        this.destroy$.next();
        this.destroy$.complete();
    }
    protected _markerLayerGroup: L.FeatureGroup;

    protected readonly mapDataService = inject(MapDataService);
    protected readonly mapStateService = inject(MapStateService);
    protected layer: L.LayerGroup = null;
    protected map: L.Map = null;
    protected markerGroups: any = {};
    protected readonly defaultMarkerGroupLayerId = "defaultGroupId";

    protected get markersFeatureGroup(): L.FeatureGroup {
        if (!this._markerLayerGroup) {
            this._markerLayerGroup = L.featureGroup().addTo(this.map);
        }
        return this._markerLayerGroup;
    }
    protected readonly mapActionsService = inject(MapActionsService);

    protected createOverviewClusterGroup(markerClusterParams: IMarkerClusterParams): void {
        this.clearMarkerGroupLayer(markerClusterParams.markClusterId);

        const leaflet = this.getMarkerClusterGroup();
        const markerCluster = leaflet.markerClusterGroup({
            iconCreateFunction: (cluster: any) => {
                return (L as any).divIcon({
                    iconSize: markerClusterParams.iconSize,
                    html: "<div></div>",
                    className: markerClusterParams.className,
                    iconAnchor: [15, 15],
                });
            },
            showCoverageOnHover: true,
            polygonOptions: {
                color: "#3d91c6",
                weight: 2,
            },
            spiderfyOnMaxZoom: markerClusterParams.spiderfyOnMaxZoom,
            chunkedLoading: markerClusterParams.chunkedLoading,
            zoomToBoundsOnClick: markerClusterParams.zoomToBoundsOnClick,
            maxClusterRadius: () => {
                return markerClusterParams.maxClusterRadius;
            },
        });

        this.addMarkerGroupLayer(markerCluster, markerClusterParams);
    }
    private clearMarkerGroupLayer(markerId: string): void {
        const id: string = Boolean(markerId) ? markerId : this.defaultMarkerGroupLayerId;
        if (Boolean(this.markerGroups[id])) {
            this.markersFeatureGroup.removeLayer(this.markerGroups[id]);
        }
    }

    private addMarkerGroupLayer(markerCluster: any, markerClusterParams: IMarkerClusterParams): void {
        const id: string = Boolean(markerClusterParams.markClusterId) ? markerClusterParams.markClusterId : this.defaultMarkerGroupLayerId;

        markerCluster.addLayers(markerClusterParams.markers);
        markerClusterParams.targetEvents.forEach((te) => {
            markerCluster.on(te, (e: any) => markerClusterParams.targetEventListener(e, te));
        });

        this.markerGroups[id] = markerCluster;
        this.markersFeatureGroup.addLayer(markerCluster).addTo(this.map);
    }
    protected createMarkerCluster(markerCluster: L.MarkerClusterGroup, markerClusterParams: IMarkerClusterParams): void {
        this.clearMarkerGroupLayer(markerClusterParams.markClusterId);

        markerCluster.on("clusterclick", (cluster: any) => {
            let clusterData = cluster?.layer?.getAllChildMarkers().map((saleMarker: any) => saleMarker.originalObject);
            clusterData = clusterData.sort((a: any, b: any) => (a.informationDate < b.informationDate ? 1 : b.informationDate < a.informationDate ? -1 : 0));

            this.openPopupForCluster(cluster.latlng, clusterData);
        });

        this.addMarkerGroupLayer(markerCluster, markerClusterParams);
    }
    protected abstract openPopupForCluster(position: { lat: number; lng: number }, sales: any[]): void;

    protected openPopupWithSales(
        htmlElement: HTMLElement,
        //        component: ComponentRef<MapPopupComponent>,
        numberOfItems: number,
        position: { lat: number; lng: number },
    ): void {
        const popupMaxHeight = 295;
        const saleItemHeight = 60;
        const headerPadding = 14;
        const offsetY = Math.min(popupMaxHeight, numberOfItems * saleItemHeight + headerPadding);

        const popup = L.popup({
            minWidth: 360,
            maxWidth: 360,
            maxHeight: 367,
            offset: L.point(35, offsetY),
            className: "cluster-popup",
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            autoPan: false,
        });

        popup.setLatLng({ lat: position.lat, lng: position.lng });
        popup.setContent(htmlElement);
        popup.openOn(this.map);
    }
    protected createMarkerPopup(content: HTMLElement | ((source: L.Layer) => HTMLElement), className: string): L.Popup {
        const offsetY = 85;

        var popup = L.popup({
            minWidth: 360,
            maxWidth: 360,
            maxHeight: 367,
            offset: L.point(50, offsetY),
            className: className,
            autoClose: true,
            closeOnClick: true,
            closeButton: true,
            autoPan: false,
        });

        // Leaflet accepts a function for lazy content: it is evaluated the first time
        // the popup opens, so we never build popup components for markers that are never clicked.
        popup.setContent(content as any);

        return popup;
    }

    protected createMarker(
        lat: number,
        lng: number,
        htmlElement: HTMLElement,
        className: string,
        originalObject: any,
        targetEvents?: string[],
        targetEventListener?: (e: any, t: string) => void,
    ): L.Marker {
        const icon = L.divIcon({
            html: htmlElement,
            className: className,
        });
        const marker = L.marker([lat, lng], { icon: icon });
        (marker as any).originalObject = originalObject;

        if (targetEvents) {
            targetEvents.forEach((et) => {
                marker.on(et, (e) => {
                    targetEventListener(e, et);
                });
            });
        }
        return marker;
    }

    /**
     * The new ng19 angular build does not connect the leaflet.markercluster to the L object properly.
     * This is a workaround to get the markerclustergroup function from the window object.
     * @returns The correct leaflet object
     */
    protected getMarkerClusterGroup() {
        if (L.markerClusterGroup != null) {
            return L;
        } else {
            return window?.L;
        }
    }

    protected closePopup() {
        this.map.closePopup();
    }
}
export interface IMarkerClusterParams {
    markClusterId?: string;
    markers: any;
    className: string;
    iconSize: number[];
    targetEvents: string[];
    spiderfyOnMaxZoom: boolean;
    chunkedLoading: boolean;
    zoomToBoundsOnClick: boolean;
    targetEventListener: (e: any, t: string) => void;
    maxClusterRadius: number;
}

export interface IMarkerParams {
    coordinates: L.LatLngExpression;
    originalObject?: object;
    targetVisual: L.Icon<L.IconOptions> | L.DivIcon;
    targetEventListener?: (e: any, t: string) => void;
    targetEvents?: string[];
    zIndexOffset?: number;
}
